import { getCurrentWindow } from "@tauri-apps/api/window";
import { withLDProvider } from "launchdarkly-react-client-sdk";
import { type ReactNode, useEffect } from "react";
import { PendingChangesSaveBar } from "src/components/PendingChangesSaveBar";
import { DesktopAppUpdateProvider } from "src/components/root/DesktopAppUpdateProvider";
import { NewVersionBanner } from "src/components/root/NewVersionBanner";
import ReactToastifyRoot from "src/components/root/ReactToastifyRoot";
import config from "src/config";
import { useDesktopNavigationShortcuts, useDesktopNavigationTracker } from "src/hooks/useDesktopNavigation";
import { useAppWindowTitle, useNewWindowLinks } from "src/hooks/useDesktopWindows";
import { useFocusSearchShortcut } from "src/hooks/useFocusSearchShortcut";
import { ModalProvider } from "src/hooks/useModal";
import { useModalStackEscapeHandler } from "src/hooks/useModalStackEscapeHandler";
import { useCaptureUrlToken } from "src/hooks/useToken";
import { isTauriRuntime } from "src/lib/runtime/environment";
import { useThemeStore } from "src/stores/themeStore";
import DrawerModalRoot from "../DrawerModalRoot";
import ReactQueryRoot from "./ReactQueryRoot";
import { useDocumentTitleIfEq } from "./routeHelpers/useDocumentTitle";
import useFavicon from "./routeHelpers/useFavicon";

const AppRootInner = ({ children }: { children?: ReactNode }) => {
  // Capture & strip ?token= magic links once, at the root — kept out of the
  // per-consumer useToken so the token isn't a router subscription everywhere.
  useCaptureUrlToken();
  useModalStackEscapeHandler();
  useDesktopNavigationTracker();
  useDesktopNavigationShortcuts();
  useNewWindowLinks();
  useAppWindowTitle();
  useFocusSearchShortcut();
  useDocumentTitleIfEq(config.brandName, "Loading...");
  useFavicon(config.brandFaviconHref);
  const theme = useThemeStore((s) => s.theme);
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    root.style.colorScheme = theme;
  }, [theme]);
  useEffect(() => {
    if (!isTauriRuntime()) return;

    void getCurrentWindow()
      .setTheme(theme)
      .catch(() => undefined);
  }, [theme]);
  return <>{children}</>;
};

const AppRoot = ({ children }: { children?: ReactNode }) => (
  <>
    <ReactQueryRoot>
      <DesktopAppUpdateProvider>
        <ModalProvider>
          <AppRootInner>{children}</AppRootInner>
        </ModalProvider>
        <PendingChangesSaveBar />
        <NewVersionBanner />
      </DesktopAppUpdateProvider>
    </ReactQueryRoot>
    <ReactToastifyRoot />
    <DrawerModalRoot />
  </>
);

export default withLDProvider({
  clientSideID: config.launchDarklyClientID,
  options: {
    streaming: false,
  },
})(AppRoot);
